// custom node example: https://g6.antv.antgroup.com/en/examples/item/customNode#jsxNode
// line animation: https://g6.antv.antgroup.com/en/examples/scatter/edge#edge
// custom node documentation: https://antv-g6.gitee.io/en/docs/manual/middle/elements/nodes/jsx-node
// library imports
import G6 from "@antv/g6";
import { useEffect, useState, useContext } from "react";
import { Card, Skeleton, theme } from "antd";
import * as _ from "@antv/util";
// image imports
import image_consumption from "../../assets/Load.png";
import image_grid from "../../assets/Grid.png";
import image_pv from "../../assets/PV.png";
import image_bess from "../../assets/BESS.png";
import image_genset from "../../assets/Generator.png";
// style imports
import "../../styles/Dashboard/SystemInformationCard.css";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// config imports
import { MQTT_REFRESH_RATE } from "../../configs/configs";

const FONT_SIZE = 13;

/**
 * prereq: state object has variables:
 *  - pv_value
 *  - load_value
 *  - grid_value
 *  - bess_value (if subscribe AI-BESS)
 *  - soc_value (if subscribe AI-BESS)
 *  - genset_value (if has genset)
 */
export default function SystemInformationCard({ containerID }) {
  // theme token
  const { token } = theme.useToken();

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // index context
  const {
    siteSelected,
    subscribeAiBess,
    notificationApi,
    hasGensetSelected,
    hasTnbSelected,
  } = useContext(IndexContext);

  // loading state
  const [loading, setLoading] = useState(true);

  // graph related variables
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [graph, setGraph] = useState(null);
  const [systemInformationData, setSystemInformationData] = useState({
    pv_value: 0,
    load_value: 0,
    grid_value: 0,
    bess_value: 0,
    soc_value: "-",
    genset_value: 0,
  });

  // register node
  G6.registerNode("label", (cfg) => {
    const title1 = cfg.title1 || "";
    const title2 = cfg.title2 || "";
    const value = cfg.value || "";
    const unit = cfg.unit || "";
    const title3 = cfg.title3 || "";

    const conditionalText =
      title1 === "BESS"
        ? `<text style={{ fill: ${token.colorTextBase}, fontSize: ${FONT_SIZE}, fontWeight: 600, marginTop: 0}}>${title3}</text>`
        : "";

    return `
      <group>
        <text style={{ fill: ${token.colorTextBase}, fontSize: ${FONT_SIZE}, fontWeight: 600}}>${title1}</text>
        <text style={{ fill: ${token.colorTextBase}, fontSize: ${FONT_SIZE}, fontWeight: 600, marginTop: -1}}>${title2}</text>
        <text style={{ fill: ${token.colorTextBase}, fontSize: ${FONT_SIZE}, fontWeight: 600, marginTop: 0}}>${value} ${unit}</text>
        ${conditionalText}
      </group>
    `;
  });

  // register edge
  G6.registerEdge(
    "polyline-dash",
    {
      afterDraw(cfg, group) {
        const shape = group.get("children")[0];
        let index = cfg.speed;
        shape.animate(
          () => {
            index += cfg.speed;
            if (index > 1000) {
              index = 0;
            }

            const res = {
              lineDash: [8, 2, 2, 4],
              lineDashOffset: -index,
            };
            return res;
          },
          {
            repeat: true,
            duration: 1000,
          },
        );
      },
    },
    "line", // extend the built-in edge
  );

  /** Function to render graph */
  function renderGraph(graphData) {
    const container = document.getElementById(containerID);
    cleanCharting(containerID);
    const width = container.scrollWidth || 500;
    const height = container.scrollHeight || 500;

    const temp = new G6.Graph({
      container: containerID,
      width,
      height,
      linkCenter: true,
      fitCenter: false,
      defaultNode: {
        type: "image",
        size: [70, 70],
      },
      defaultEdge: {
        type: "polyline-dash",
        style: { lineWidth: 4 },
      },
    });
    temp.data(graphData);
    temp.render();

    setGraph(temp);
  }

  /** Function to clean graph */
  function cleanCharting(container) {
    document.getElementById(container).innerHTML = "";
  }

  /** API to get system info card data */
  function getSystemInfo() {
    fetchWrapper({
      endpoint_url: "dashboard/getSystemInfo",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSystemInformationData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get real-time system information.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  // ============================================================
  // Shared helpers
  // ============================================================

  const formatMetric = (value) => ({
    value:
      value !== "-"
        ? value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "-",
    unit: value !== "-" ? "kW" : "",
  });

  const STATUS = {
    pv: (v) =>
      v === "-"
        ? "DPM Offline"
        : v > 0
          ? "Producing"
          : v < 0
            ? "Consuming"
            : "Idle",
    bess: (v) =>
      v == 0
        ? "Idle"
        : v === "-"
          ? "Offline"
          : v < 0
            ? "Charging"
            : "Discharging",
    load: (v) => (v === "-" ? "DPM Offline" : v === 0 ? "Idle" : "Consuming"),
    grid: (v) =>
      v == "-"
        ? "DPM Offline"
        : v > 0
          ? "Importing"
          : v < 0
            ? "Exporting"
            : "Idle",
    // NOTE: genset status logic genuinely differs between modes in the original code —
    // preserved both variants rather than merging them. Flag if this was unintentional.
    gensetWithAiBess: (v) =>
      v === "-" ? "Offline" : v > 0 ? "Importing" : "Idle",
    gensetStandalone: (v) =>
      v == 0 ? "Idle" : v === "-" ? "Offline" : "Importing",
  };

  function makeNodePair({
    key,
    img,
    imgPos,
    labelPos,
    title1,
    value,
    statusFn,
    extraLabelFields = {},
  }) {
    const { value: fv, unit } = formatMetric(value);
    return [
      { id: `${key}image`, img, x: imgPos.x, y: imgPos.y },
      {
        x: labelPos.x,
        y: labelPos.y,
        title1,
        title2: statusFn(value),
        ...extraLabelFields,
        value: fv,
        unit,
        id: `${key}node`,
        type: "label",
      },
    ];
  }

  // ============================================================
  // Layout position tables — one entry per mode combination
  // ============================================================

  const LAYOUTS = {
    // subscribeAiBess = true, hasGensetSelected = true, hasTnbSelected = true (grid present)
    aiAndGenset: {
      pv: { imgPos: { x: 280, y: 30 }, labelPos: { x: 320, y: 15 } },
      bess: { imgPos: { x: 38, y: 208 }, labelPos: { x: 0, y: 110 } },
      load: { imgPos: { x: 165, y: 120 }, labelPos: { x: 145, y: 150 } },
      grid: { imgPos: { x: 280, y: 215 }, labelPos: { x: 320, y: 195 } },
      genset: { imgPos: { x: 38, y: 30 }, labelPos: { x: 80, y: 5 } },
    },

    // subscribeAiBess = true, hasGensetSelected = true, hasTnbSelected = false (no grid)
    // PV/BESS/Load stay where they are in aiAndGenset; genset takes over grid's old slot.
    aiAndGensetNoGrid: {
      pv: { imgPos: { x: 155, y: 30 }, labelPos: { x: 195, y: 15 } },
      bess: { imgPos: { x: 280, y: 208 }, labelPos: { x: 320, y: 143 } },
      load: { imgPos: { x: 155, y: 120 }, labelPos: { x: 135, y: 150 } },
      genset: { imgPos: { x: 28, y: 208 }, labelPos: { x: 0, y: 120 } }, // <- old grid slot
    },

    // subscribeAiBess = true, hasGensetSelected = false
    aiOnly: {
      pv: { imgPos: { x: 155, y: 30 }, labelPos: { x: 195, y: 15 } },
      bess: { imgPos: { x: 280, y: 208 }, labelPos: { x: 320, y: 143 } },
      load: { imgPos: { x: 155, y: 120 }, labelPos: { x: 135, y: 150 } },
      grid: { imgPos: { x: 38, y: 208 }, labelPos: { x: 0, y: 120 } },
    },

    // subscribeAiBess = false, hasGensetSelected = true, hasTnbSelected = true (grid present)
    gensetOnly: {
      pv: { imgPos: { x: 155, y: 30 }, labelPos: { x: 195, y: 15 } },
      genset: { imgPos: { x: 280, y: 208 }, labelPos: { x: 310, y: 150 } },
      load: { imgPos: { x: 155, y: 120 }, labelPos: { x: 135, y: 150 } },
      grid: { imgPos: { x: 38, y: 208 }, labelPos: { x: 0, y: 120 } },
    },

    // subscribeAiBess = false, hasGensetSelected = true, hasTnbSelected = false (no grid)
    // Genset takes over grid's old position since grid is not present at this site.
    gensetOnlyNoGrid: {
      pv: { imgPos: { x: 155, y: 30 }, labelPos: { x: 195, y: 15 } },
      load: { imgPos: { x: 155, y: 120 }, labelPos: { x: 135, y: 150 } },
      genset: { imgPos: { x: 38, y: 208 }, labelPos: { x: 0, y: 120 } }, // <- old grid slot
    },

    // subscribeAiBess = false, hasGensetSelected = false
    basic: {
      pv: { imgPos: { x: 120, y: 40 }, labelPos: { x: 15, y: 10 } },
      load: { imgPos: { x: 270, y: 120 }, labelPos: { x: 315, y: 100 } },
      grid: { imgPos: { x: 120, y: 170 }, labelPos: { x: 15, y: 150 } },
    },
  };

  // ============================================================
  // Node builder
  // ============================================================

  function buildGraphNodes(
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
    systemInformationData,
  ) {
    const layoutKey = subscribeAiBess
      ? hasGensetSelected
        ? hasTnbSelected
          ? "aiAndGenset"
          : "aiAndGensetNoGrid"
        : "aiOnly"
      : hasGensetSelected
        ? hasTnbSelected
          ? "gensetOnly"
          : "gensetOnlyNoGrid"
        : "basic";

    const layout = LAYOUTS[layoutKey];
    const nodes = [];

    if (layout.pv) {
      nodes.push(
        ...makeNodePair({
          key: "pv",
          img: image_pv,
          ...layout.pv,
          title1: "PV",
          value: systemInformationData.pv_value,
          statusFn: STATUS.pv,
        }),
      );
    }

    if (layout.bess) {
      const soc = systemInformationData.soc_value;
      nodes.push(
        ...makeNodePair({
          key: "bess",
          img: image_bess,
          ...layout.bess,
          title1: "BESS",
          value: systemInformationData.bess_value,
          statusFn: STATUS.bess,
          extraLabelFields: {
            title3: `SOC: ${soc !== "-" ? soc.toFixed(1) + "%" : soc}`,
            image: image_bess,
          },
        }),
      );
    }

    if (layout.load) {
      nodes.push(
        ...makeNodePair({
          key: "load",
          img: image_consumption,
          ...layout.load,
          title1: "Load",
          value: systemInformationData.load_value,
          statusFn: STATUS.load,
          extraLabelFields: { image: image_consumption },
        }),
      );
    }

    if (layout.grid) {
      nodes.push(
        ...makeNodePair({
          key: "grid",
          img: image_grid,
          ...layout.grid,
          title1: "Grid",
          value: systemInformationData.grid_value,
          statusFn: STATUS.grid,
          extraLabelFields: { labelRight: false, image: image_grid },
        }),
      );
    }

    if (layout.genset) {
      nodes.push(
        ...makeNodePair({
          key: "genset",
          img: image_genset,
          ...layout.genset,
          title1: "Genset",
          value: systemInformationData.genset_value,
          statusFn: subscribeAiBess
            ? STATUS.gensetWithAiBess
            : STATUS.gensetStandalone,
          extraLabelFields: subscribeAiBess ? {} : { image: image_genset },
        }),
      );
    }

    return nodes;
  }

  // ============================================================
  // Edge builder
  // ============================================================

  function buildGraphEdges(
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
    systemInformationData,
  ) {
    const edges = [];
    const { pv_value, bess_value, load_value, grid_value, genset_value } =
      systemInformationData;

    // PV producing
    if (pv_value > 0) {
      if (load_value > 0) {
        edges.push({
          source: "pvimage",
          target: "loadimage",
          speed: 1,
          color: "#696969",
        });
      }
      // Export to grid only when TNB is present
      if (hasTnbSelected && grid_value < 0) {
        edges.push({
          source: "pvimage",
          target: "gridimage",
          speed: 0.4,
          color: "#696969",
        });
      }
    }
    // PV consuming power
    else if (pv_value < 0) {
      if (hasTnbSelected && grid_value > bess_value) {
        // Grid → PV
        edges.push({
          source: "gridimage",
          target: "pvimage",
          speed: 1,
          color: "#696969",
        });
      } else if (subscribeAiBess) {
        // BESS → PV; when TNB present, route via grid as intermediary
        if (hasTnbSelected) {
          edges.push({
            source: "bessimage",
            target: "gridimage",
            speed: 1,
            color: "#696969",
          });
          edges.push({
            source: "gridimage",
            target: "pvimage",
            speed: 1,
            color: "#696969",
          });
        } else {
          edges.push({
            source: "bessimage",
            target: "pvimage",
            speed: 1,
            color: "#696969",
          });
        }
      }
    }

    // BESS edges (only when AI-BESS subscribed)
    if (subscribeAiBess) {
      if (bess_value > 0) {
        // BESS discharging → Load
        if (load_value > 0) {
          edges.push({
            source: "bessimage",
            target: "loadimage",
            speed: 1,
            color: "#696969",
          });
        }
      } else if (bess_value < 0) {
        // BESS charging
        if (hasTnbSelected) {
          if (grid_value < Math.abs(bess_value)) {
            // Primary source is PV; when TNB + genset present, route via grid
            if (hasGensetSelected) {
              edges.push({
                source: "pvimage",
                target: "gridimage",
                speed: 1,
                color: "#696969",
              });
              edges.push({
                source: "gridimage",
                target: "bessimage",
                speed: 1,
                color: "#696969",
              });
            } else if (pv_value > 0) {
              edges.push({
                source: "pvimage",
                target: "bessimage",
                speed: 1,
                color: "#696969",
              });
            }
          } else {
            // Charging from TNB
            edges.push({
              source: "gridimage",
              target: "bessimage",
              speed: 1,
              color: "#696969",
            });
          }
        } else {
          // No TNB: charge from PV if producing, otherwise from genset
          if (pv_value > 0) {
            edges.push({
              source: "pvimage",
              target: "bessimage",
              speed: 1,
              color: "#696969",
            });
          } else if (hasGensetSelected && genset_value > 0) {
            edges.push({
              source: "gensetimage",
              target: "bessimage",
              speed: 1,
              color: "#696969",
            });
          }
        }
      }
    }

    // Grid → Load (TNB importing)
    if (hasTnbSelected && grid_value > 0 && load_value > 0) {
      edges.push({
        source: "gridimage",
        target: "loadimage",
        speed: 0.4,
        color: "#666666",
      });
    }

    // Genset → Load
    if (hasGensetSelected && genset_value > 0 && load_value > 0) {
      edges.push({
        source: "gensetimage",
        target: "loadimage",
        speed: 1,
        color: "#666666",
      });
    }

    return edges;
  }

  // ============================================================
  // Usage inside the component
  // ============================================================

  useEffect(() => {
    // UPDATE NODE
    // Node arrangement depends on:
    // - subscribeAiBess: whether the site subscribes to AI-BESS
    // - hasGensetSelected: whether the site has a genset
    // - hasTnbSelected: whether the site has a grid (TNB) connection
    //   (only relevant when hasGensetSelected is true and subscribeAiBess is false;
    //   in that case, if there's no grid, genset takes over the grid's old position)
    const graphData = {
      nodes: buildGraphNodes(
        subscribeAiBess,
        hasGensetSelected,
        hasTnbSelected,
        systemInformationData,
      ),
      edges: buildGraphEdges(
        subscribeAiBess,
        hasGensetSelected,
        hasTnbSelected,
        systemInformationData,
      ),
    };
    if (isFirstRender) {
      setIsFirstRender(false);
      if (subscribeAiBess !== null && !loading) {
        renderGraph(graphData);
      }
    } else {
      if (subscribeAiBess !== null && !loading) {
        if (graph) {
          graph.changeData(graphData);
        }
        renderGraph(graphData);
      }
    }
  }, [
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
    systemInformationData,
    token,
    loading,
  ]);

  useEffect(() => {
    if (!graph) return;
    const container = document.getElementById(containerID);
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width && height) {
          graph.changeSize(width, height);
          graph.fitView();
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [graph, containerID]);

  useEffect(() => {
    if (siteSelected) {
      setLoading(true);
      getSystemInfo();

      const timer = setInterval(() => {
        getSystemInfo();
      }, MQTT_REFRESH_RATE * 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [siteSelected]);

  return (
    <>
      <Card size="small" title={"System Information"}>
        {loading || subscribeAiBess === null ? (
          <Skeleton
            active={true}
            style={{ padding: "1rem", margin: "auto" }}
            paragraph={{ rows: 5 }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              height: "260px",
            }}
          >
            <div
              id={containerID}
              style={{
                width: "100%",
                height: "260px",
                display: loading ? "none" : "flex",
              }}
            />
          </div>
        )}
      </Card>
    </>
  );
}
